// ============================================
// FILE: src/pages/BuyPage.jsx
// ============================================
import React, { useState, useEffect, useRef } from 'react';
import PropertyCard from '../components/PropertyCard';
import { properties } from '../data/properties';
import {
  Search, SlidersHorizontal, MapPin, Home, Building2,
  Warehouse, BedDouble, X, ArrowUpDown, Sparkles, TrendingUp
} from 'lucide-react';

/* ─── Keyframes & global styles ─── */
const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,700;0,800;1,700&family=Outfit:wght@300;400;500;600;700&display=swap');

  :root {
    --terra: #c8602a;
    --terra-light: #e8845a;
    --terra-pale: #fdf0e8;
    --cream: #faf7f2;
    --ink: #1a1208;
    --muted: #8a7968;
    --border: #e8e0d4;
    --white: #ffffff;
    --shadow-warm: rgba(200,96,42,0.15);
  }

  @keyframes fadeSlideUp {
    from { opacity: 0; transform: translateY(40px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes fadeIn {
    from { opacity: 0; } to { opacity: 1; }
  }
  @keyframes marqueeScroll {
    from { transform: translateX(0); }
    to   { transform: translateX(-50%); }
  }
  @keyframes floatBlob {
    0%,100% { transform: translate(0,0) scale(1); }
    33%      { transform: translate(20px,-15px) scale(1.04); }
    66%      { transform: translate(-10px,10px) scale(0.97); }
  }
  @keyframes scaleIn {
    from { opacity: 0; transform: scale(0.9); }
    to   { opacity: 1; transform: scale(1); }
  }

  .buy-page { font-family: 'Outfit', sans-serif; }
  .buy-page h1, .buy-page h2 { font-family: 'Playfair Display', serif; }

  .filter-pill {
    display: inline-flex; align-items: center; gap: 6px;
    padding: 9px 18px; border-radius: 999px;
    font-size: 13px; font-weight: 600; cursor: pointer;
    border: 1.5px solid var(--border);
    background: var(--white); color: var(--muted);
    transition: all 0.25s ease; white-space: nowrap;
    font-family: 'Outfit', sans-serif;
  }
  .filter-pill:hover { border-color: var(--terra); color: var(--terra); background: var(--terra-pale); }
  .filter-pill.active { background: var(--terra); color: #fff; border-color: var(--terra); box-shadow: 0 4px 14px var(--shadow-warm); }

  .search-bar {
    display: flex; align-items: center; gap: 12px;
    background: var(--white); border: 1.5px solid var(--border);
    border-radius: 16px; padding: 12px 20px;
    box-shadow: 0 4px 20px rgba(0,0,0,0.06);
    transition: border-color 0.25s, box-shadow 0.25s;
  }
  .search-bar:focus-within { border-color: var(--terra); box-shadow: 0 4px 20px var(--shadow-warm); }
  .search-bar input {
    flex: 1; border: none; outline: none; background: transparent;
    font-family: 'Outfit', sans-serif; font-size: 15px; color: var(--ink);
  }
  .search-bar input::placeholder { color: var(--muted); }

  .sort-select {
    appearance: none; background: var(--white);
    border: 1.5px solid var(--border); border-radius: 12px;
    padding: 10px 40px 10px 14px; font-family: 'Outfit', sans-serif;
    font-size: 13px; font-weight: 600; color: var(--ink);
    cursor: pointer; outline: none; transition: border-color 0.25s;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%238a7968' stroke-width='2.5'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E");
    background-repeat: no-repeat; background-position: right 12px center;
  }
  .sort-select:focus { border-color: var(--terra); }

  .prop-card-wrap { opacity: 0; animation: fadeSlideUp 0.5s ease forwards; }

  .count-badge {
    display: inline-flex; align-items: center; gap: 5px;
    background: var(--terra-pale); color: var(--terra);
    border: 1px solid rgba(200,96,42,0.2);
    border-radius: 999px; padding: 4px 14px;
    font-size: 13px; font-weight: 700; font-family: 'Outfit', sans-serif;
  }

  .marquee-track {
    display: flex; gap: 0;
    animation: marqueeScroll 22s linear infinite;
    width: max-content;
  }
  .marquee-item {
    display: inline-flex; align-items: center; gap: 8px;
    padding: 0 28px; font-size: 12px; font-weight: 600;
    letter-spacing: 0.08em; color: var(--muted); white-space: nowrap;
    text-transform: uppercase;
  }
  .marquee-dot { width: 4px; height: 4px; border-radius: 50%; background: var(--terra); }

  .empty-state { grid-column: 1 / -1; text-align: center; padding: 80px 20px; animation: scaleIn 0.4s ease forwards; }
`;

/* ─── Hook: visible on scroll ─── */
function useVisible(threshold = 0.1) {
  const ref = useRef(null);
  const [vis, setVis] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVis(true); }, { threshold });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return [ref, vis];
}

/* ─── Marquee strip ─── */
const MarqueeStrip = () => {
  const items = ['Smart Investments', 'RERA Approved', 'Bank Loan Ready', 'Verified Listings', 'Legal Cleared', 'High ROI Zones', 'Gated Communities', 'Ready to Move'];
  return (
    <div style={{ overflow: 'hidden', borderTop: '1px solid var(--border)', borderBottom: '1px solid var(--border)', padding: '10px 0', background: 'var(--cream)' }}>
      <div className="marquee-track">
        {[...items, ...items].map((item, i) => (
          <span className="marquee-item" key={i}><span className="marquee-dot" /> {item}</span>
        ))}
      </div>
    </div>
  );
};

/* ─── Main page ─── */
const BuyPage = () => {
  const buyProperties = properties.filter(p => p.type === 'Buy');

  const [search, setSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState('All');
  const [sortBy, setSortBy] = useState('default');
  const [showFilters, setShowFilters] = useState(false);
  const [heroRef, heroVis] = useVisible(0.05);

  const filters = [
    { label: 'All',       icon: <Home size={14} /> },
    { label: 'Apartment', icon: <Building2 size={14} /> },
    { label: 'Studio',    icon: <Home size={14} /> },
    { label: 'Villa',     icon: <Warehouse size={14} /> },
    { label: 'Penthouse', icon: <Building2 size={14} /> },
    { label: '1 BHK',     icon: <BedDouble size={14} /> },
    { label: '2 BHK',     icon: <BedDouble size={14} /> },
    { label: '3 BHK',     icon: <BedDouble size={14} /> },
    { label: '4+ BHK',    icon: <BedDouble size={14} /> },
  ];

  const parsePrice = (str = '') => {
    const s = str.replace(/[₹,\s]/g, '');
    const num = parseFloat(s);
    if (s.includes('Cr'))   return num * 10_000_000;
    if (s.includes('Lakh')) return num * 100_000;
    return num;
  };

  const matchesFilter = (p, filter) => {
    if (filter === 'All') return true;
    if (filter === '1 BHK')  return p.bedrooms === 1;
    if (filter === '2 BHK')  return p.bedrooms === 2;
    if (filter === '3 BHK')  return p.bedrooms === 3;
    if (filter === '4+ BHK') return p.bedrooms >= 4;
    return p.title?.toLowerCase().includes(filter.toLowerCase());
  };

  const filtered = buyProperties
    .filter(p => {
      const q = search.toLowerCase().trim();
      const matchSearch = !q || p.title?.toLowerCase().includes(q) || p.location?.toLowerCase().includes(q) || p.bedrooms?.toString().includes(q);
      return matchSearch && matchesFilter(p, activeFilter);
    })
    .sort((a, b) => {
      if (sortBy === 'price-asc')  return parsePrice(a.price) - parsePrice(b.price);
      if (sortBy === 'price-desc') return parsePrice(b.price) - parsePrice(a.price);
      return 0;
    });

  return (
    <div className="buy-page" style={{ minHeight: '100vh', background: 'var(--cream)', position: 'relative', overflow: 'hidden' }}>
      <style>{STYLES}</style>

      {/* ── Ambient background blobs ── */}
      <div style={{ position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 0 }}>
        <div style={{ position: 'absolute', top: '-160px', right: '-120px', width: '600px', height: '600px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(200,96,42,0.07) 0%, transparent 65%)', animation: 'floatBlob 12s ease-in-out infinite' }} />
        <div style={{ position: 'absolute', bottom: '0', left: '-100px', width: '450px', height: '450px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(200,96,42,0.05) 0%, transparent 65%)', animation: 'floatBlob 16s ease-in-out infinite 3s' }} />
        <div style={{ position: 'absolute', inset: 0, opacity: 0.025, backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 0L40 40M40 0L0 40' stroke='%23c8602a' stroke-width='0.5'/%3E%3C/svg%3E")`, backgroundSize: '40px 40px' }} />
      </div>

      {/* ── HERO ── */}
      <div ref={heroRef} style={{ position: 'relative', zIndex: 1, padding: '72px 24px 56px', textAlign: 'center' }}>

        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: '8px',
          border: '1px solid rgba(200,96,42,0.3)', borderRadius: '999px',
          padding: '6px 18px', marginBottom: '28px',
          background: 'rgba(200,96,42,0.06)',
          opacity: heroVis ? 1 : 0,
          animation: heroVis ? 'fadeIn 0.6s ease forwards' : 'none',
        }}>
          <Sparkles size={13} color="var(--terra)" />
          <span style={{ fontSize: '12px', fontWeight: '700', color: 'var(--terra)', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
            Investment & Dream Homes
          </span>
        </div>

        <h1 style={{
          fontSize: 'clamp(42px, 7vw, 88px)', color: 'var(--ink)',
          lineHeight: 1.0, margin: '0 0 12px', letterSpacing: '-0.03em',
          opacity: heroVis ? 1 : 0,
          animation: heroVis ? 'fadeSlideUp 0.65s ease 0.1s forwards' : 'none',
        }}>
          Invest in Your<br />
          <em style={{ color: 'var(--terra)', fontStyle: 'italic' }}>Future Today</em>
        </h1>

        <p style={{
          fontSize: '17px', color: 'var(--muted)', maxWidth: '460px', margin: '16px auto 40px',
          lineHeight: 1.7, fontWeight: 300,
          opacity: heroVis ? 1 : 0,
          animation: heroVis ? 'fadeSlideUp 0.65s ease 0.22s forwards' : 'none',
        }}>
          Discover verified properties to buy — from smart starter homes to high-yield investment assets in prime locations.
        </p>

        {/* Search bar */}
        <div style={{
          maxWidth: '560px', margin: '0 auto',
          opacity: heroVis ? 1 : 0,
          animation: heroVis ? 'fadeSlideUp 0.65s ease 0.34s forwards' : 'none',
        }}>
          <div className="search-bar">
            <Search size={18} color="var(--muted)" />
            <input
              type="text"
              placeholder="Search by name, location, BHK…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            {search && (
              <button onClick={() => setSearch('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--muted)', display: 'flex' }}>
                <X size={16} />
              </button>
            )}
            <div style={{ width: '1px', height: '20px', background: 'var(--border)' }} />
            <MapPin size={16} color="var(--terra)" />
          </div>
        </div>
      </div>

      {/* ── Marquee ── */}
      <MarqueeStrip />

      {/* ── Filters & sort bar ── */}
      <div style={{ position: 'relative', zIndex: 1, maxWidth: '1200px', margin: '0 auto', padding: '28px 24px 0' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
            <button className={`filter-pill ${showFilters ? 'active' : ''}`} onClick={() => setShowFilters(v => !v)}>
              <SlidersHorizontal size={14} /> Filters
            </button>
            <div style={{ width: '1px', height: '24px', background: 'var(--border)' }} />
            {filters.map(f => (
              <button key={f.label} className={`filter-pill ${activeFilter === f.label ? 'active' : ''}`} onClick={() => setActiveFilter(f.label)}>
                {f.icon} {f.label}
              </button>
            ))}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span className="count-badge"><Home size={12} /> {filtered.length} properties</span>
            <select className="sort-select" value={sortBy} onChange={e => setSortBy(e.target.value)}>
              <option value="default">Sort: Default</option>
              <option value="price-asc">Price: Low → High</option>
              <option value="price-desc">Price: High → Low</option>
            </select>
          </div>
        </div>

        {/* Expandable advanced filters */}
        <div style={{ maxHeight: showFilters ? '120px' : '0', overflow: 'hidden', transition: 'max-height 0.4s cubic-bezier(0.4,0,0.2,1)' }}>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', padding: '20px 0 4px' }}>
            {['Ready to Move', 'Under Construction', 'New Launch', 'Resale', 'Gated Community', 'Corner Unit', 'East Facing'].map(tag => (
              <button key={tag} className="filter-pill" style={{ fontSize: '12px', padding: '7px 14px' }}>{tag}</button>
            ))}
          </div>
        </div>
      </div>

      {/* ── Property Grid ── */}
      <div style={{ position: 'relative', zIndex: 1, maxWidth: '1200px', margin: '0 auto', padding: '32px 24px 80px' }}>

        {/* Section label */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '28px' }}>
          <div style={{ flex: 1, height: '1px', background: 'linear-gradient(90deg, var(--border), transparent)' }} />
          <span style={{ fontSize: '12px', fontWeight: '700', color: 'var(--muted)', letterSpacing: '0.12em', textTransform: 'uppercase' }}>
            {activeFilter === 'All' ? 'All Listings' : activeFilter}
          </span>
          <div style={{ flex: 1, height: '1px', background: 'linear-gradient(90deg, transparent, var(--border))' }} />
        </div>

        {filtered.length === 0 ? (
          <div className="empty-state">
            <div style={{ fontSize: '56px', marginBottom: '16px' }}>🏡</div>
            <h2 style={{ fontSize: '24px', color: 'var(--ink)', marginBottom: '8px', fontFamily: 'Playfair Display, serif' }}>No Matches Found</h2>
            <p style={{ color: 'var(--muted)', fontSize: '15px' }}>Try adjusting your filters or search term.</p>
            <button
              onClick={() => { setSearch(''); setActiveFilter('All'); }}
              style={{ marginTop: '20px', background: 'var(--terra)', color: '#fff', border: 'none', borderRadius: '12px', padding: '12px 28px', fontFamily: 'Outfit,sans-serif', fontWeight: '600', fontSize: '14px', cursor: 'pointer', boxShadow: '0 6px 20px var(--shadow-warm)' }}
            >
              Clear All Filters
            </button>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '28px' }}>
            {filtered.map((property, i) => (
              <div key={property.id} className="prop-card-wrap" style={{ animationDelay: `${i * 0.07}s` }}>
                <PropertyCard property={property} />
              </div>
            ))}
          </div>
        )}

        {/* ── Bottom CTA strip ── */}
        {filtered.length > 0 && (
          <div style={{
            marginTop: '64px', padding: '36px 40px',
            background: 'linear-gradient(135deg,#1a1208 0%,#2d1f0e 100%)',
            borderRadius: '24px', display: 'flex',
            alignItems: 'center', justifyContent: 'space-between',
            flexWrap: 'wrap', gap: '20px',
            boxShadow: '0 20px 56px rgba(26,18,8,0.25)',
            position: 'relative', overflow: 'hidden',
            animation: 'fadeSlideUp 0.6s ease 0.4s both',
          }}>
            <div style={{ position: 'absolute', right: '-60px', top: '-60px', width: '220px', height: '220px', borderRadius: '50%', border: '2px solid rgba(200,96,42,0.15)' }} />
            <div style={{ position: 'absolute', right: '40px', bottom: '-30px', width: '120px', height: '120px', borderRadius: '50%', border: '2px solid rgba(200,96,42,0.1)' }} />
            <div>
              <h2 style={{ fontSize: '22px', color: '#fff', margin: '0 0 6px', fontFamily: 'Playfair Display, serif' }}>
                Need guidance on your investment?
              </h2>
              <p style={{ color: 'rgba(255,255,255,0.55)', margin: 0, fontSize: '14px' }}>
                Our experts will help you find the right property — at the right price.
              </p>
            </div>
            <a
              href="/inquiry"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '10px',
                background: 'var(--terra)', color: '#fff',
                borderRadius: '14px', padding: '14px 28px',
                fontWeight: '700', fontSize: '15px',
                textDecoration: 'none', fontFamily: 'Outfit, sans-serif',
                boxShadow: '0 8px 28px rgba(200,96,42,0.4)',
                transition: 'transform 0.2s, box-shadow 0.2s',
                flexShrink: 0,
              }}
              onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.04)'; e.currentTarget.style.boxShadow = '0 12px 36px rgba(200,96,42,0.5)'; }}
              onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = '0 8px 28px rgba(200,96,42,0.4)'; }}
            >
              Talk to an Agent <TrendingUp size={16} />
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

export default BuyPage;